import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { colors } from "../../../shared/colors";
import { sharedMutators } from "../../../shared/mutators";
import { useMouseDownOutside } from "../../hooks/useMouseDownOutside";
import { useNavigateOnBranchChange } from "../../hooks/useNavigateOnBranchChange";
import { playClick } from "../../lib/sounds";
import { runStepOptimistically } from "../../lib/runStepOptimistically";
import { writeToClipboard } from "../../lib/writeToClipboard";
import { zIndexes } from "../../lib/zIndexes";
import { Icon } from "../Icon";
import { InfoIcon } from "../InfoIcon";
import { MenuItem } from "../MenuItem";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import type { StepData } from "../Workflow";

const Menu = styled("div")`
  position: fixed;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  padding: 4px;
  z-index: ${zIndexes.contextMenu};
  min-width: 180px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Divider = styled("div")`
  height: 0.5px;
  margin: 4px 8px;
  background: var(--border-alpha);
`;

const ColorRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px;
`;

type Props = {
  step: StepData;
  x: number;
  y: number;
  workflowId: string;
  branchId: string;
  commitId: string;
  canWrite: boolean;
  copyableUrl: string | null;
  copyableEmail: string | null;
  onClose: () => void;
};

export function StepContextMenu({
  step,
  x,
  y,
  workflowId,
  branchId,
  commitId,
  canWrite,
  copyableUrl,
  copyableEmail,
  onClose,
}: Props) {
  const z = useZero();
  const navigateOnBranchChange = useNavigateOnBranchChange(branchId);
  const [containerRef, menuRef] = useMouseDownOutside(onClose);

  const handleRun = useCallback(
    (ephemeral: boolean) => {
      if (!canWrite) return;
      runStepOptimistically(z, {
        workflowId,
        branchId,
        commitId,
        stepDir: step.name,
        ephemeral,
      });
      onClose();
    },
    [z, workflowId, branchId, commitId, step.name, onClose, canWrite]
  );

  const handleColorChange = useCallback(
    (color: string) => {
      if (!canWrite) return;
      void navigateOnBranchChange((branchParams) =>
        z.mutate(
          sharedMutators.updateStepColor({
            ...branchParams,
            stepDir: step.name,
            color,
          })
        )
      );
      onClose();
    },
    [z, navigateOnBranchChange, onClose, step.name, canWrite]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const currentColor = step.color;
  const adjustedX = Math.min(x, window.innerWidth - 200);

  return createPortal(
    <div ref={containerRef} style={{ display: "contents" }}>
      <Menu
        ref={menuRef}
        data-popover
        data-direction="up"
        style={{ left: adjustedX, bottom: window.innerHeight - y }}
      >
        <MenuItem disabled={!canWrite} onClick={() => handleRun(true)}>
          <Icon name="solo-run" size={16} />
          <Text variant="text-regular">Solo run</Text>
          <Tooltip
            content={
              <div style={{ width: 200 }}>
                <Text variant="text-small-md">
                  Runs only this step, without triggering downstream steps.
                </Text>
              </div>
            }
          >
            <InfoIcon>
              <Icon name="info" size={12} />
            </InfoIcon>
          </Tooltip>
        </MenuItem>
        <MenuItem disabled={!canWrite} onClick={() => handleRun(false)}>
          <Icon name="run" size={16} />
          <Text variant="text-regular">Run</Text>
          <Tooltip
            content={
              <div style={{ width: 200 }}>
                <Text variant="text-small-md">
                  Runs this step and its downstream steps.
                </Text>
              </div>
            }
          >
            <InfoIcon>
              <Icon name="info" size={12} />
            </InfoIcon>
          </Tooltip>
        </MenuItem>
        {copyableUrl && (
          <>
            <MenuItem
              onClick={() => {
                void writeToClipboard(copyableUrl);
                onClose();
              }}
            >
              <Icon name="link-2" size={16} />
              <Text variant="text-regular">Copy URL</Text>
            </MenuItem>
            <MenuItem
              onClick={() => {
                window.open(copyableUrl, "_blank");
                onClose();
              }}
            >
              <Icon name="agent-open" size={16} />
              <Text variant="text-regular">Open in new tab</Text>
            </MenuItem>
          </>
        )}
        {copyableEmail && (
          <>
            <MenuItem
              onClick={() => {
                void writeToClipboard(copyableEmail);
                onClose();
              }}
            >
              <Icon name="at-sign" size={16} />
              <Text variant="text-regular">Copy email address</Text>
            </MenuItem>
            <MenuItem
              onClick={() => {
                window.open(`mailto:${copyableEmail}`, "_blank");
                onClose();
              }}
            >
              <Icon name="agent-open" size={16} />
              <Text variant="text-regular">Compose email</Text>
            </MenuItem>
          </>
        )}
        <Divider />
        <ColorRow
          style={canWrite ? undefined : { opacity: 0.5, pointerEvents: "none" }}
        >
          {colors.map((color) => (
            <ColorDot
              key={color}
              color={color}
              isActive={currentColor === color}
              onSelect={() => handleColorChange(color)}
            />
          ))}
        </ColorRow>
      </Menu>
    </div>,
    document.body
  );
}

const DotButton = styled("button")`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 0;
  padding: 0;
  cursor: pointer;
  outline: 0;
  box-shadow: inset 0 0 0 1px var(--alpha4);
`;

function ColorDot({
  color,
  isActive,
  onSelect,
}: {
  color: string;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <DotButton
      onMouseDown={() => {
        playClick();
        onSelect();
      }}
      className={isActive ? "active" : ""}
      style={{
        background: `var(--${color}-600)`,
        boxShadow: isActive
          ? `inset 0 0 0 1px var(--alpha4), 0 0 0 1px var(--surface-primary), 0 0 0 2px var(--${color}-600)`
          : `inset 0 0 0 1px var(--alpha4)`,
      }}
      onMouseEnter={(e) => {
        if (!isActive) {
          e.currentTarget.style.boxShadow = `inset 0 0 0 1px var(--alpha4), 0 0 0 1px var(--surface-primary), 0 0 0 2px color-mix(in srgb, var(--${color}-600) 50%, transparent)`;
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          e.currentTarget.style.boxShadow = "inset 0 0 0 1px var(--alpha4)";
        }
      }}
    />
  );
}
